import { CSSProperties, useEffect, useMemo, useState } from 'react';
import styles from './BundleSelector.scss';
import { updateBrowserControlState } from '@wg/web2clientapi/browser/updateBrowserControlState';
import { groupBundlesByName } from '~/utils/bundles';
import ButtonEsc from '~/components/ButtonEsc/ButtonEsc';
import history from '~/utils/history';
import useKeyDown, { KEYS_CODE } from '~/hooks/useKeyDown';
import { t } from '~/utils/localization';
import CategoryTimer from '~/components/CategoryTimer/CategoryTimer';
import BundleSelectorItem from './BundleSelectorItem/BundleSelectorItem';
import BundleInfo from './BundleInfo/BundleInfo';
import { useAppSelector } from '~/Store';
import { items } from '@wg/wows-entities/const';
import Account from '~/account/Account';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { appActions } from '~/Store/appSlice';
import events, { CustomEventNames } from '~/utils/eventEmitter';
import { getSearchParam, isInGameBrowser, updateSearchParam } from '~/utils/utils';
import { closeBrowser } from '@wg/web2clientapi/browser/closeBrowser';

interface IBundleSelector {
    category: ICategory;
    bundlesIds: number[];
}

const SEARCH_KEY = 'selectedTab';

const BundleSelector = ({ category, bundlesIds }: IBundleSelector) => {
    const dispatch = useDispatch();
    const popups = useAppSelector((state) => state.app.popups);
    const [isFinished, setFinishFlag] = useState(false);

    const groups = useMemo(() => {
        return groupBundlesByName(bundlesIds);
    }, []);

    const firstKey = Object.keys(groups)[0];
    const defaultSelectedIndex = useMemo(() => {
        const index = groups[firstKey].findIndex((item) => {
            if (item.primaryItem?.type === items.VEHICLES) {
                const [isRecieved] = Account.getInventoryInfoByType(items.VEHICLES, item.primaryItem.identifier);
                return !isRecieved;
            }
        });
        return index >= 0 ? index : 0;
    }, []);

    const [selectedBundleIndex, setSelectedBundleIndex] = useState(+getSearchParam(SEARCH_KEY) || defaultSelectedIndex);

    const close = () => {
        if (popups.length > 0) {
            return;
        }
        if (isInGameBrowser) {
            closeBrowser();
        } else {
            history.back();
        }
    };

    const onComplete = () => {
        setFinishFlag(true);
    };

    useEffect(() => {
        updateBrowserControlState(true);
        dispatch(appActions.setFullscreen(true));

        events.on(CustomEventNames.closedRewardsPopup, close);

        return () => {
            updateBrowserControlState(false);
            dispatch(appActions.setFullscreen(false));
            events.off(CustomEventNames.closedRewardsPopup, close);
        };
    }, []);

    useEffect(() => {
        if (!popups.length) {
            updateBrowserControlState(true);
        }
    }, [popups]);

    useKeyDown(close, [KEYS_CODE.ESC]);

    const style: CSSProperties = {};
    if (category.background) {
        style['backgroundImage'] = `url(${category.background})`;
    }

    return (
        <div className={styles.wrapper} style={style}>
            <div className={styles.backgroundWrapper}>
                {groups[firstKey].map((bundle, index) => {
                    return (
                        <div
                            key={`bundle_background_${bundle.id}`}
                            style={{ backgroundImage: `url(${bundle.icons.innerBackground})` }}
                            className={classNames(styles.background, {
                                [styles.visible]: index === selectedBundleIndex,
                            })}
                        />
                    );
                })}
            </div>
            <div className={styles.closeWrapper}>
                <ButtonEsc onClick={close} />
            </div>
            {!isFinished && <div className={styles.mobileContent}>{t('This feature is available only for PC')}</div>}
            {isFinished && <div className={styles.noticyContent}>{t('Товары в этой категории более недоступны')}</div>}
            {!isFinished && (
                <div className={styles.view}>
                    <div className={styles.header}>
                        <div className={styles.title}>{category.title}</div>
                        {category.activeTill && category.showCountdown && (
                            <div className={styles.timerWrapper}>
                                <CategoryTimer category={category} className={styles.categoryTimerWrapper} title={category.description} onComplete={onComplete} />
                            </div>
                        )}
                    </div>
                    <div className={styles.content}>
                        <div className={styles.leftContent}>
                            <div className={styles.items}>
                                {groups[firstKey].map((bundle, index) => {
                                    return (
                                        <BundleSelectorItem
                                            key={`bundle_selector_${bundle.id}`}
                                            bundle={bundle}
                                            isSelected={index === selectedBundleIndex}
                                            index={index}
                                            onClick={(n) => {
                                                if (n !== selectedBundleIndex) {
                                                    setSelectedBundleIndex(n);
                                                    updateSearchParam({ [SEARCH_KEY]: '' + n });
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                        <div className={styles.rightContent}>
                            <BundleInfo bundle={groups[firstKey][selectedBundleIndex]} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BundleSelector;
